import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { navigate } from 'gatsby';
import AppContext from './AppContext';
import AppToast from './AppToast';
import Notification from '../UI/Notification';
import useUser from '../../hooks/useUser';
import {
  CleanLayout,
  LoyaltyLayout,
  MigrationProcessing,
  OtherLinesLayout,
  SiteLayout,
  WebpayLayout,
  NotFoundLayout,
  ForbiddenLayout,
} from '../Layouts';
import OtherLinesProvider from '../../contextProvider/OtherLinesProvider';
import Navbar from '../Navbar/Navbar';
import { ChatModal, ChatFeedback } from '../Help/Chat';
import MainLayoutContainer from './styles/MainLayoutStyled';
import * as custom from '../Menu/CustomEntries';
import IdentityValidationLayout from '../IdentityValidation/IdentityValidationLayout';

const PATHNAME = pathname =>
  pathname !== '/' ? pathname.replace(/^\/+|\/+$/g, '') : pathname;

const MainLayout = ({ children, location }) => {
  const modals = {
    planzero: 'PlanZero',
    extrasaldo: 'ExtraBalance',
    recargas: 'Recharge',
  };
  const { showChatbot, setShowChatbot } = useContext(AppContext);
  const searchParams = new URLSearchParams(location.search);

  const modalParam = searchParams.get('modal');

  const showChatFeedback = searchParams.get('utm_source') === 'chatbot';
  const pathname = PATHNAME(location.pathname);
  const user = useUser();
  const ModalComponent = modals[modalParam] && custom[modals[modalParam]];

  /* --Qualtrics Implementation-- */
  useEffect(() => {
    if (!ModalComponent && modalParam) {
      navigate('/404');
    }

    if (typeof window !== 'undefined' && 'QSI' in window) {
      const params = {};
      if (user) {
        params.NUMERO_LINEA = user.plan.number;
        params.SEGMENTO = user.plan.segment;
        params.PLAN = user.plan.ratePlan;
        params.TIPO_CLIENTE = user.user.isCorporate ? 'business' : 'consumer';
        params.PERFILAMIENTO = user.plan.profile;
      }

      const qsiApi = window.QSI.API;
      if (qsiApi) {
        window.Q_EED_PARAMS = btoa(JSON.stringify(params).toUpperCase());
        qsiApi.unload();
        qsiApi.load();
        qsiApi.run();
      }
    }
  }, [location]);
  /* --Ends Qualtrics Implementation-- */

  const RouteComponent = {
    'mis-otras-lineas/detalle': (
      <OtherLinesLayout location={location}>{children}</OtherLinesLayout>
    ),
    webpay: <WebpayLayout location={location}>{children}</WebpayLayout>,
    'migracion/processing': (
      <MigrationProcessing location={location}>{children}</MigrationProcessing>
    ),
    'portal-cautivo': <CleanLayout withLogoHeader>{children}</CleanLayout>,
    'inscripcion-equipos': (
      <LoyaltyLayout location={location}>{children}</LoyaltyLayout>
    ),
    404: <NotFoundLayout>{children}</NotFoundLayout>,
    403: <ForbiddenLayout>{children}</ForbiddenLayout>,
  };

  const isOtherLines =
    pathname !== 'mis-otras-lineas/detalle' &&
    pathname.includes('mis-otras-lineas');
  const siteContent = isOtherLines ? (
    <OtherLinesProvider>{children}</OtherLinesProvider>
  ) : (
    children
  );

  const isIdentityValidationLayout = [
    'validacion-identidad/contexto',
    'validacion-identidad/en-proceso',
    'validacion-identidad/success',
    'validacion-identidad/failed',
    'validacion-identidad/timeout',
    'validacion-identidad/maximo-sms',
    'vdicambioplan',
  ].includes(pathname);

  const contentComponent = () => {
    let content;
    const isBenefitsComponent = pathname.split('/')[0] === 'beneficios';

    if (isIdentityValidationLayout) {
      content = <IdentityValidationLayout>{children}</IdentityValidationLayout>;
    } else if (isBenefitsComponent) {
      content = <LoyaltyLayout location={location}>{children}</LoyaltyLayout>;
    } else {
      content = (
        <>
          {RouteComponent[pathname] || (
            <SiteLayout location={location}>{siteContent}</SiteLayout>
          )}
          {pathname !== 'portal-cautivo' && <Navbar />}
          {showChatbot && <ChatModal hide={() => setShowChatbot(false)} />}
          {showChatFeedback && <ChatFeedback />}
          {pathname === 'home' && ModalComponent && (
            <ModalComponent baseComponent="div" autoOpen>
              <></>
            </ModalComponent>
          )}
        </>
      );
    }

    return content;
  };

  return (
    <MainLayoutContainer id="main-layout">
      <Notification />
      {contentComponent()}
      <AppToast />
    </MainLayoutContainer>
  );
};

MainLayout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.instanceOf(Object).isRequired,
};

export default MainLayout;
